import { colors } from '../../../../colors'
import { FREE_THEME_ID } from '../../../../constants'
import type { Theme } from '../../../../types'
import type React from 'react'

interface ThemeCheckboxFieldProps {
  selectedThemeId: number | null
  postableThemes: Theme[]
  onChange: (themeId: number | null) => void
  labelWidth?: number
}

const getStyles = (labelWidth: number): Record<string, React.CSSProperties> => ({
  field: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontWeight: 'bold',
    fontSize: '14px',
    marginBottom: '4px',
    pointerEvents: 'none' as const,
    width: `${labelWidth}px`,
    flexShrink: 0,
    marginRight: '8px',
  },
  optional: {
    color: colors['text-secondary'],
    marginLeft: '4px',
    fontSize: '12px',
  },
  inputContainer: {
    flex: 1,
  },
  checkboxList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'flex-start' as const,
    padding: '8px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors['border-light'],
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  checkboxItemSelected: {
    backgroundColor: colors['keijiban-coral-bg'],
    borderColor: colors['keijiban-coral'],
    borderWidth: '1px',
    padding: '8px',
  },
  checkbox: {
    display: 'none',
  },
  checkboxContent: {
    flex: 1,
  },
  checkIcon: {
    marginLeft: '8px',
    fontSize: '18px',
    color: colors['keijiban-coral'],
  },
  themeTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: colors['text-default'],
    marginBottom: '4px',
  },
  themeDescription: {
    fontSize: '12px',
    color: colors['text-secondary'],
    lineHeight: '1.5',
  },
  themePeriod: {
    fontSize: '12px',
    color: colors['keijiban-coral'],
    marginTop: '4px',
  },
  note: {
    fontSize: '12px',
    color: colors['text-secondary'],
    marginTop: '8px',
    padding: '8px',
    backgroundColor: colors['background-light'],
    borderRadius: '4px',
  },
})

const ThemeCheckboxField: React.FC<ThemeCheckboxFieldProps> = ({
  selectedThemeId,
  postableThemes,
  onChange,
  labelWidth = 80,
}) => {
  const styles = getStyles(labelWidth)
  // 自由テーマを除外した募集中のテーマのみを表示
  const regularThemes = postableThemes.filter((theme) => theme.id !== FREE_THEME_ID)

  const handleCheckboxChange = (themeId: number) => {
    // 既に選択されているテーマをクリックした場合は選択解除
    if (selectedThemeId === themeId) {
      onChange(null)
    } else {
      onChange(themeId)
    }
  }

  return (
    <div style={styles.field}>
      <div style={styles.label}>
        今月のお題
        <br />
        に追加
      </div>
      <div style={styles.inputContainer}>
        {regularThemes.length > 0 ? (
          <>
            <div style={styles.checkboxList}>
              {regularThemes.map((theme) => (
                <label
                  key={theme.id}
                  style={{
                    ...styles.checkboxItem,
                    ...(selectedThemeId === theme.id ? styles.checkboxItemSelected : {}),
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedThemeId === theme.id}
                    onChange={() => handleCheckboxChange(theme.id)}
                    style={styles.checkbox}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleCheckboxChange(theme.id)
                      }
                    }}
                  />
                  <div style={styles.checkboxContent}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          ...styles.themeTitle,
                          ...(selectedThemeId === theme.id
                            ? { color: colors['keijiban-brown'] }
                            : {}),
                        }}
                      >
                        ＃{theme.title}
                      </div>
                      {selectedThemeId === theme.id && (
                        <span className="icon_font icf_ok" style={styles.checkIcon} /> // TODO: fix icon class
                      )}
                    </div>
                    <div
                      style={{
                        ...styles.themeDescription,
                        ...(selectedThemeId === theme.id
                          ? { color: colors['keijiban-brown'] }
                          : {}),
                      }}
                    >
                      {theme.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </>
        ) : (
          <div style={styles.note}>
            現在募集中のテーマはありません。自由テーマでの投稿となります。
          </div>
        )}
      </div>
    </div>
  )
}

export default ThemeCheckboxField
